/**
 * Map Plugin for Native Interactive Site Map
 *
 * Manages the Google Maps instance lifecycle: creation and rendering.
 * Renders the map centered on the community position from Redux state.
 *
 * Single responsibility: create and manage the map canvas.
 *
 * @module nativeInteractiveSiteMap/plugins/map.plugin
 */

const defaultMapSettings = {
  clickableIcons: false,
  disableDefaultUI: true,
  disableDoubleClickZoom: false,
  gestureHandling: 'greedy',
  minZoom: 12,
  maxZoom: 21,
  panControl: false,
  scrollwheel: true,
  zoomControl: true,
  zoomControlStyle: 'SMALL',
  zoom: 17,
  styles: [
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.school', stylers: [{ visibility: 'on' }] },
  ],
};

export default function createMapPlugin() {
  const state = {
    mapFacade: null,
    dom: { root: null },
    subscriptions: [],
  };

  return {
    name: 'map',

    async init(context) {
      context.state.map = {
        started: false,
        ready: false,
      };
    },

    async start(context) {
      if (context.state.map?.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      if (!state.mapFacade) {
        return;
      }

      state.dom.root = context.root.querySelector('[data-map-element]');
      if (!state.dom.root) {
        return;
      }

      state.subscriptions.push(
        context.services.mapLibraryLoaded$.subscribe(() => {
          const { latitude, longitude } = context.state.community ?? {};
          const centerPosition = { lat: latitude, lng: longitude };

          if (Number.isFinite(centerPosition.lat) && Number.isFinite(centerPosition.lng)) {
            state.mapFacade.createMap({
              container: state.dom.root,
              options: {
                ...defaultMapSettings,
                center: centerPosition,
              },
            });
            // Set map as ready in global state so that dependent plugins can start
            context.state.map = {
              started: true,
              ready: true,
            };
          }
        })
      );
    },

    async stop(context) {
      if (context.state.map) {
        context.state.map.started = false;
        context.state.map.ready = false;
      }
      state.subscriptions.forEach((subscription) => {
        subscription?.unsubscribe?.();
      });
      state.subscriptions = [];
      state.mapFacade?.destroyMap?.();
      state.dom.root = null;
      state.mapFacade = null;
      delete context.state.map;
    },
  };
}